import React, { useState } from 'react';
import { List, LayoutGrid, Columns, Settings, X } from 'lucide-react';

const ViewSelector = ({ 
  currentView, 
  onViewChange, 
  visibleFields, 
  onFieldsChange, 
  availableFields 
}) => {
  const [showFieldSelector, setShowFieldSelector] = useState(false);

  const views = [
    { id: 'list', icon: List, label: 'List View', color: 'blue' },
    { id: 'detailed', icon: LayoutGrid, label: 'Detailed View', color: 'indigo' },
    { id: 'kanban', icon: Columns, label: 'Kanban View', color: 'purple' }
  ];

  const handleFieldToggle = (field) => {
    if (visibleFields.includes(field)) {
      onFieldsChange(visibleFields.filter(f => f !== field));
    } else {
      onFieldsChange([...visibleFields, field]);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center space-x-2">
        {/* View Toggle Buttons */}
        {views.map((view) => {
          const Icon = view.icon;
          const isActive = currentView === view.id;
          
          return (
            <button
              key={view.id}
              onClick={() => onViewChange(view.id)}
              className={`flex items-center px-4 py-2 rounded-lg transition-all ${
                isActive
                  ? `bg-${view.color}-600 text-white shadow-md`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={view.label}
            >
              <Icon size={18} className="mr-2" />
              <span className="hidden md:inline">{view.label.replace(' View', '')}</span>
            </button>
          );
        })}

        {/* Field Selector Button (for list and detailed views) */}
        {(currentView === 'list' || currentView === 'detailed') && (
          <button
            onClick={() => setShowFieldSelector(!showFieldSelector)}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            title="Configure Fields"
          >
            <Settings size={18} className="mr-2" />
            <span className="hidden md:inline">Fields</span>
          </button>
        )}
      </div>

      {/* Field Selector Modal */}
      {showFieldSelector && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold">Configure Visible Fields</h3>
            <button
              onClick={() => setShowFieldSelector(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="p-4 max-h-80 overflow-y-auto">
            <div className="space-y-2">
              {availableFields.map((field) => (
                <label
                  key={field.key}
                  className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={visibleFields.includes(field.key)}
                    onChange={() => handleFieldToggle(field.key)}
                    className="mr-3 h-4 w-4 text-indigo-600 rounded"
                  />
                  <span className="text-sm font-medium">{field.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="p-4 border-t bg-gray-50 flex justify-between">
            <button
              onClick={() => onFieldsChange(availableFields.map(f => f.key))}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Select All
            </button>
            <button
              onClick={() => setShowFieldSelector(false)}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewSelector;
